import axios from 'axios';
import { API_BASE_URL } from '../config/endpoints.js';
import { generateRequestId } from '../observability/requestId.js';
import { refreshAuthToken } from './auth.js';
import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  updateRefreshToken,
  clearTokens,
} from './tokenStorage.js';
import { isCognitoMode } from './authMode.js';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

let onAuthFailure = null;
let refreshPromise = null;

const setAuthFailureHandler = (handler) => {
  onAuthFailure = handler;
};

apiClient.interceptors.request.use((config) => {
  const requestConfig = config;
  requestConfig.headers = requestConfig.headers || {};
  requestConfig.headers['X-Request-ID'] =
    requestConfig.headers['X-Request-ID'] || generateRequestId('api');

  const token = getAccessToken();
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }

  return requestConfig;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isRefreshRequest = originalRequest?.url?.includes('/api/token/refresh');

    if (status !== 401 || originalRequest?._retry || isRefreshRequest) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isCognitoMode()) {
      clearTokens();
      if (typeof onAuthFailure === 'function') {
        onAuthFailure();
      }
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        const refreshToken = getRefreshToken();
        refreshPromise = refreshAuthToken(refreshToken);
      }
      const refreshed = await refreshPromise;
      updateAccessToken(refreshed.access_token);
      updateRefreshToken(refreshed.refresh_token);
      originalRequest.headers.Authorization = `Bearer ${refreshed.access_token}`;
      return await apiClient(originalRequest);
    } catch (refreshError) {
      clearTokens();
      if (typeof onAuthFailure === 'function') {
        onAuthFailure();
      }
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
    }
  }
);

export { apiClient, setAuthFailureHandler };
